<template>
  <div class="asset-container">
    <!-- User Controls -->
    <div class="controls" style="position: sticky; top: 0; z-index: 1000;">
      <div class="left-controls">
        <button class="refresh-button" @click="refreshDataClick" title="Refresh">
          <span class="material-icons">refresh</span>
        </button>
        <button class="refresh-button" @click="downloadData" title="Download" :disabled="downloading">
          <span class="material-icons" :class="{ 'loading': downloading }">download</span>
        </button>
        <p>{{ $t('dashboard.updated') }} {{ formattedLastUpdated }}</p>
      </div>
      <div class="right-controls">
        <div class="autocomplete-container">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="$t('views.navbar.searchobjects')"
            class="autocomplete-input select-s2 browser-default small-contents"
            @focus="showSuggestions = true"
            @input="onInput"
            @keydown.up="moveSelection(-1)"
            @keydown.down="moveSelection(1)"
            @keydown.enter="selectHighlightedSuggestion"
            @keydown.esc="clearSearchClick"
          >
          <ul class="suggestions-list" v-if="showSuggestions && filteredSuggestions.length > 0">
            <li
              v-for="(suggestion, index) in filteredSuggestions"
              :key="suggestion"
              class="suggestion-item"
              :class="{ 'highlighted': index === selectedIndex }"
              @click="selectSuggestion(suggestion)"
            >
              {{ suggestion }}
            </li>
          </ul>
        </div>
        <button class="refresh-button" @click="clearSearchClick" title="Clear Search">
          <span class="material-icons">clear</span>
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <span class="error-icon">⚠️</span> {{ errorMessage }}
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!errorMessage && (!filteredData || filteredData.length === 0)">
      {{ $t('dashboard.nodataavailable')}}
    </div>

    <!-- Table with Pagination -->
    <div v-else class="table-container">
      <div class="table-wrapper">
        <table class="asset-table">
          <thead>
            <tr>
              <th @click="sort('LastMeasurementDateTime')">
                {{$t('views.dashboard.list.lastmeasurementdatetime')}}
                <span v-if="sortKey === 'LastMeasurementDateTime' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('Asset')">
                {{ $t('views.navbar.asset')}}
                <span v-if="sortKey === 'Asset' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('AssetPath')">
                {{ $t('views.alarmlist.location')}}
                <span v-if="sortKey === 'AssetPath' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('Status')">
                {{$t('views.dashboard.list.status')}}
                <span v-if="sortKey === 'Status' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('DirectionNCount')" v-b-tooltip.hover.right :title="$t('dashboard.nofthrowsn')">
                {{ this.$t('charts.n') }}
                <span v-if="sortKey === 'DirectionNCount' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('DirectionRCount')" v-b-tooltip.hover.right :title="$t('dashboard.nofthrowsr')">
                {{ this.$t('charts.r') }}
                <span v-if="sortKey === 'DirectionRCount' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('AlarmsCount')">
                {{$t('dashboard.alarms')}}
                <span v-if="sortKey === 'AlarmsCount' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('LastEMailDateTime')">
                {{  $t('dashboard.lastemail') }}
                <span v-if="sortKey === 'LastEMailDateTime' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('LastMaintenancedateTime')">
                {{ $t('views.switch.lastmaintenancedate')}}
                <span v-if="sortKey === 'LastMaintenancedateTime' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('MonitoredBy')">
                {{$t('dashboard.monitoredby')}}
                <span v-if="sortKey === 'MonitoredBy' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('AssetType')">
                {{ $t('views.switch.alarmsettings.assettype') }}
                <span v-if="sortKey === 'AssetType' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="asset in paginatedData" :key="asset.RailObjectID">
              <td>{{ formatDate(asset.LastMeasurementDateTime) }}</td>
              <td class="clickable" @click="goToPath(asset.RailObjectID)">
                {{ asset.Asset || '-' }}
              </td>
              <td class="clickable" @click="goToPath(asset.RailObjectID)">
                {{ asset.AssetPath }}
              </td>
              <td :class="getStatusClass(asset.Status)">{{ asset.Status || '-' }}</td>
              <td>{{ asset.DirectionNCount }}</td>
              <td>{{ asset.DirectionRCount }}</td>
              <td :class="getAlarmClass(asset.AlarmsCount)">{{ asset.AlarmsCount }}</td>
              <td>{{ formatDate(asset.LastEMailDateTime) }}</td>
              <td>{{ formatDate(asset.LastMaintenancedateTime) }}</td>
              <td>{{ asset.MonitoredBy }}</td>
              <td>{{ translateAssetType(asset.AssetType) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div v-if="paginatedData.length > 0" class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
          <span class="material-icons">navigate_before</span>
        </button>
        <span>{{ $t('dashboard.page')}} {{ currentPage }} {{ $t('dashboard.of')}} {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pagination-btn">
          <span class="material-icons">navigate_next</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'

export default {
  name: 'AssetView',
  props: ['companyId', 'features'],
  emits: ['updating'],
  data () {
    return {
      loading: false,
      downloading: false,
      currentUser: AuthenticationService.getUser(),
      errorMessage: null,
      assetData: [],
      processedData: [],
      searchItems: {},
      currentPage: 1,
      rowsPerPage: 5,
      sortKey: '',
      sortDirection: 'default',
      searchQuery: '',
      selectedSearchItem: '',
      showSuggestions: false,
      selectedIndex: -1,
      isSelecting: false,
      lastUpdated: new Date(),
      showMessage: false
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateRowsPerPage)
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.updateRowsPerPage)
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    companyId: {
      immediate: true,
      async handler (newCompanyId) {
        if (newCompanyId) {
          // Also clears search query when company id has changed
          await this.refreshData(newCompanyId)
        } else {
          this.processedData = []
          this.searchItems = {}
        }
      }
    },

    searchQuery (newVal) {
      if (!newVal) {
        this.showSuggestions = false
        this.selectedIndex = -1
      } else if (!this.isSelecting) {
        this.showSuggestions = true
        this.selectedIndex = -1
      }
      this.updateTable()
    },

    filteredSuggestions () {
      if (this.selectedIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (this.selectedIndex < -1) {
        this.selectedIndex = -1
      }
    }
  },
  computed: {
    formattedLastUpdated () {
      if (!this.lastUpdated) return 'N/A'

      const date = new Date(this.lastUpdated)
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },

    filteredData () {
      let filteredData = [...this.processedData]
      // Apply filtering based on selected search item (RailObjectId)
      if (this.selectedSearchItem && this.searchItems[this.selectedSearchItem]) {
        const filteredRailObjectIds = this.searchItems[this.selectedSearchItem]
        filteredData = filteredData.filter(item =>
          filteredRailObjectIds.includes(item.RailObjectID)
        )
      }
      return filteredData
    },

    paginatedData () {
      let sortedData = [...this.filteredData]

      // Apply sorting only if not in 'default' state
      if (this.sortDirection !== 'default' && this.sortKey) {
        sortedData.sort((a, b) => {
          let valueA = a[this.sortKey] || ''
          let valueB = b[this.sortKey] || ''

          // Special handling for date fields
          if (this.sortKey === 'LastMeasurementDateTime' || this.sortKey === 'LastEMailDateTime' || this.sortKey === 'LastMaintenancedateTime') {
            // Handle invalid or missing dates
            const dateA = valueA === '-' ? new Date('0001-01-01') : new Date(valueA)
            const dateB = valueB === '-' ? new Date('0001-01-01') : new Date(valueB)
            valueA = dateA.getTime()
            valueB = dateB.getTime()
          } else if (this.sortKey === 'DirectionNCount' || this.sortKey === 'DirectionRCount' || this.sortKey === 'AlarmsCount') {
            valueA = Number(valueA) || 0
            valueB = Number(valueB) || 0
          }

          // Handle numeric sorting
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA
          }

          // Default to string sorting
          return this.sortDirection === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA)
        })
      }
      const start = (this.currentPage - 1) * this.rowsPerPage
      const end = start + this.rowsPerPage
      return sortedData.slice(start, end)
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage))
    },

    filteredSuggestions () {
      if (!this.searchQuery) return Object.keys(this.searchItems)
      const query = this.searchQuery.toLowerCase()
      return Object.keys(this.searchItems).filter(item =>
        item.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    async fetchAssetData (companyId, featureId) {
      this.loading = true
      this.errorMessage = null
      try {
        const [assetResponse, searchResponse] = await Promise.all([
          DashboardService.getAssetOverview(this.currentUser.UserId, companyId),
          DashboardService.getLastStatusSearchItems(this.currentUser.UserId, companyId)
        ])
        this.assetData = assetResponse.data
        this.searchItems = searchResponse.data
        this.processData()
        this.lastUpdated = Date.now()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.errorMessage = 'Failed to load data: ' + error.response.data.error
        } else {
          this.errorMessage = error.message || 'Failed to load data. Please try again.'
        }
        this.assetData = []
        this.searchItems = {}
      }
      this.loading = false
      this.$nextTick(() => {
        this.updateRowsPerPage()
      })
    },

    processData () {
      if (!this.assetData || this.assetData.length === 0) return
      this.processedData = this.assetData.map(asset => ({
        ...asset,
        LastMeasurementDateTime: asset.LastMeasurementDateTime || '-',
        LastEMailDateTime: asset.LastEMailDateTime.includes('0001-01-01') ? '-' : asset.LastEMailDateTime,
        LastMaintenancedateTime: asset.LastMaintenancedateTime.includes('0001-01-01') ? '-' : asset.LastMaintenancedateTime
      }))
    },

    sort (key) {
      if (this.sortKey === key) {
        // Cycle through states: asc -> desc -> default
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc'
        } else if (this.sortDirection === 'desc') {
          this.sortDirection = 'default'
        } else {
          this.sortDirection = 'asc'
        }
      } else {
        // New column clicked, start with ascending
        this.sortKey = key
        this.sortDirection = 'asc'
      }
      this.currentPage = 1
    },

    formatDate (dateStr) {
      if (!dateStr || dateStr === '-' || dateStr.includes('0001-01-01')) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },

    translateAssetType (assetstr) {
      if (!assetstr || assetstr === '-') return this.$t('unknown')
      switch (assetstr) {
        case 'Switch':
          return this.$t('views.alarmlist.switch')
        case 'ComplexData':
          return this.$t('dashboard.mixedsignals')
        default:
          return this.$t('unknown')
      }
    },

    getStatusClass (status) {
      return status === 'Error' ? 'bg-red' : (status === 'Ok' ? 'bg-green' : '')
    },

    getAlarmClass (count) {
      return count > 0 ? 'bg-orange' : ''
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    updateRowsPerPage () {
      this.$nextTick(() => {
        const tableWrapper = this.$el.querySelector('.table-wrapper')
        const tableHeader = this.$el.querySelector('.asset-table thead')
        if (tableWrapper && tableHeader) {
          const rowHeight = 32 // Row height
          const headerHeight = tableHeader.clientHeight || 40 // Header height
          const paginationHeight = 43 // Pagination controls

          // Calculate available height for table rows
          const availableHeight = tableWrapper.clientHeight - headerHeight - paginationHeight

          // Calculate max number of rows that fit
          this.rowsPerPage = Math.max(1, Math.floor(availableHeight / rowHeight))

          // Ensure pagination updates
          if (this.currentPage > this.totalPages) {
            this.currentPage = Math.max(1, this.totalPages)
          }
        }
      })
    },

    emitSelectedAsset (parentId) {
      this.$emit('asset-selected', parentId)
    },

    async refreshDataClick () {
      this.$emit('updating')
      await this.refreshData()
    },

    async refreshData () {
      if (this.companyId) {
        this.currentPage = 1
        this.sortKey = ''
        this.sortDirection = 'default'
        this.selectedSearchItem = ''
        this.searchQuery = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        await this.fetchAssetData(this.companyId, 1)
      }
    },

    clearSearchClick () {
      this.selectedSearchItem = ''
      this.searchQuery = ''
      this.showSuggestions = false
      this.selectedIndex = -1
      this.isSelecting = false
      this.updateTable()
    },

    selectSuggestion (suggestion) {
      this.isSelecting = true
      this.selectedSearchItem = suggestion
      this.searchQuery = suggestion
      this.showSuggestions = false
      this.selectedIndex = -1
      this.$nextTick(() => {
        this.isSelecting = false
      })
      this.updateTable()
    },

    moveSelection (delta) {
      if (!this.showSuggestions || this.filteredSuggestions.length === 0) return
      const newIndex = this.selectedIndex + delta
      if (newIndex >= 0 && newIndex < this.filteredSuggestions.length) {
        this.selectedIndex = newIndex
      } else if (newIndex < 0) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (newIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = 0
      }
    },

    selectHighlightedSuggestion () {
      this.isSelecting = true
      if (this.searchQuery.trim() === '') {
        this.selectedSearchItem = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        this.isSelecting = false
        this.updateTable()
      } else if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredSuggestions.length) {
        this.selectSuggestion(this.filteredSuggestions[this.selectedIndex])
      } else if (this.filteredSuggestions.length > 0) {
        this.selectSuggestion(this.filteredSuggestions[0])
      }
      this.$nextTick(() => {
        this.showSuggestions = false
      })
    },

    onInput () {
      if (!this.isSelecting && this.searchQuery) {
        this.showSuggestions = true
      }
    },

    handleClickOutside (event) {
      const autocompleteContainer = this.$el.querySelector('.autocomplete-container')
      if (autocompleteContainer && !autocompleteContainer.contains(event.target)) {
        this.showSuggestions = false
        this.selectedIndex = -1
      }
    },

    updateTable () {
      this.currentPage = 1
    },

    async downloadData () {
      if (this.downloading) return

      this.downloading = true
      try {
        const response = await DashboardService.getAssetOverviewExport(
          this.currentUser.UserId,
          this.companyId,
          { responseType: 'blob' }
        )

        const baseFilename = 'asset_export'
        const timestamp = new Date().toISOString().replace(/[:.-]/g, '').slice(0, 14)
        const companyIdOrName = this.companyName || this.companyId || 'Unknown'
        const filename = `${baseFilename}_${companyIdOrName}_${timestamp}.xlsx`

        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Failed to download the file'
      } finally {
        this.downloading = false
      }
    },

    handleMouseOver () {
      this.showMessage = !this.showMessage
    },

    goToPath (railObjectId) {
      this.$router.push(`/switch/${railObjectId}`)
    }
  }
}
</script>

<style scoped>
/* Main container */
.asset-container {
  background: white;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Table */
.table-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.asset-table {
  width: 100%;
  border-collapse: collapse;
}

.asset-table th,
.asset-table td {
  border: 1px solid #ccc;
  padding: 5px;
  text-align: center;
}

.asset-table th {
  background-color: #f4f4f4;
  cursor: pointer;
  user-select: none;
}

.asset-table th:hover {
  background-color: #e0e0e0;
}

/* Pagination */
.pagination {
  position: sticky;
  bottom: 4px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0;
  height: 24px;
  box-sizing: border-box;
  z-index: 1;
}

.pagination-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 24px;
  transition: color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
}

.pagination-btn .material-icons {
  font-size: 24px;
  color: #666;
}

.pagination-btn:hover:not(:disabled) .material-icons {
  color: #3498db;
}

/* Spinner */
.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  z-index: 10;
}

.spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  border: 1px solid #d32f2f;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  margin: 10px;
  width: fit-content;
  max-width: 100%;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.error-icon {
  margin-right: 8px;
  font-size: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Colors */
.bg-red {
  background-color: rgba(255, 0, 0, 0.432);
}

.bg-orange {
  background-color: rgba(255, 165, 0, 0.3);
}

.bg-green {
  background-color: rgba(0, 255, 0, 0.3);
}

/* Add User Controls Styles */
.controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 12px;
  height: 34px;
  flex-wrap: nowrap;
}

.left-controls {
  display: flex;
  gap: 5px;
  align-items: center;
}

.right-controls {
  display: flex;
  gap: 5px;
  align-items: center;
  position: relative;
}

.controls .refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  height: 24px;
  line-height: 24px;
}

.controls .refresh-button .material-icons {
  font-size: 20px;
  color: #666;
}

.controls .refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.autocomplete-container {
  position: relative;
}

.autocomplete-input {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: 175px;
  box-sizing: border-box;
}

.suggestions-list {
  position: absolute;
  top: 28px;
  left: 0;
  width: 175px;
  max-height: 150px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 1001;
}

.suggestion-item {
  padding: 2px 4px;
  cursor: pointer;
  font-size: 12px;
  color: #333;
}

.suggestion-item.highlighted {
  background-color: #f4f4f4;
}

.suggestion-item:hover {
  background-color: #e0e0e0;
}

.asset-table td.clickable {
  cursor: pointer;
}

.asset-table td.clickable:hover {
  color: #1e6bb8;
}

.tooltip {
  position: relative;
  display: inline-block;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  border-bottom: 1px dotted black;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard3/_AssetView.vue